import React from 'react';

// What the page on screen asks of the top bar, side menu and footer; a page that asks nothing gets them as they are
export interface PageChrome {
    // Fits the top bar and side menu to the smallest screens
    compact?: boolean;
    // Shown in the footer in place of the tools
    footer?: React.ReactNode;
}

const PageChromeContext = React.createContext<PageChrome>({});
const SetPageChromeContext = React.createContext<(chrome: PageChrome) => void>(() => undefined);

// Holds the chrome the page on screen has set, for the top bar, side menu and footer to read
export const PageChromeProvider = ({ children }: { children: React.ReactNode }) => {
    const [chrome, setChrome] = React.useState<PageChrome>({});
    return (
        <SetPageChromeContext.Provider value={setChrome}>
            <PageChromeContext.Provider value={chrome}>{children}</PageChromeContext.Provider>
        </SetPageChromeContext.Provider>
    );
};

export const usePageChrome = (): PageChrome => React.useContext(PageChromeContext);

// Sets the chrome while the calling page is mounted. A layout effect sets it before the first paint, so the page never
// shows a frame of the other chrome. Pass a constant: a new object each render would set it again each render
export const useSetPageChrome = (chrome: PageChrome) => {
    const setChrome = React.useContext(SetPageChromeContext);
    React.useLayoutEffect(() => {
        setChrome(chrome);
        return () => setChrome({});
    }, [setChrome, chrome]);
};
